import React, { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight, CheckCircle, AlertCircle, Lightbulb, BookOpen, Users, MapPin, Clock, HelpCircle, Target, FileText, Sparkles } from 'lucide-react';
import { Button, Card, Input } from './common';
import TextArea from './common/TextArea';
import { useAssistant } from '../contexts/AssistantContext';
import { supabase } from '../supabaseClient';
import { theme } from '../styles/theme';
import OpenAI from 'openai';

// Initialize OpenAI client (similar to EnhancedTextArea.js)
const openai = new OpenAI({
  apiKey: process.env.REACT_APP_OPENAI_API_KEY,
  dangerouslyAllowBrowser: true,
  defaultHeaders: {
    'OpenAI-Beta': 'assistants=v2'
  }
});

// Animated Score Component
const AnimatedScore = ({ targetScore, duration = 2000, suffix = '' }) => {
  const [currentScore, setCurrentScore] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);

  useEffect(() => {
    if (targetScore > 0) {
      setIsAnimating(true);
      const increment = targetScore / (duration / 50);
      let current = 0;
      
      const timer = setInterval(() => {
        current += increment;
        if (current >= targetScore) {
          setCurrentScore(targetScore);
          setIsAnimating(false);
          clearInterval(timer);
        } else {
          setCurrentScore(Math.floor(current));
        }
      }, 50);

      return () => clearInterval(timer);
    }
  }, [targetScore, duration]);

  return (
    <span className={`font-bold text-2xl transition-all duration-300 ${
      isAnimating ? 'scale-110' : 'scale-100'
    }`}>
      {currentScore}{suffix}
    </span>
  );
};

const FlashcardProblemDefinition = ({ initialStatement, projectId, onComplete }) => {
  const [currentCard, setCurrentCard] = useState(0);
  const [problemData, setProblemData] = useState({
    full_statement: '',
    short_description: '',
    what: '',
    where: '',
    when: '',
    who: '',
    how: '',
    why: ''
  });
  const [cardCompletionStatus, setCardCompletionStatus] = useState({});
  const [aiValidation, setAiValidation] = useState(null);
  const [isValidating, setIsValidating] = useState(false);
  const [isEnhancing, setIsEnhancing] = useState(false);
  const [saveStatus, setSaveStatus] = useState('');
  const [error, setError] = useState('');
  const [cardTransition, setCardTransition] = useState('center');

  const { addAssistantMessage, sendMessage, updateContext } = useAssistant();

  const cards = [
    {
      id: 'overview',
      title: 'Problem Definition Overview',
      icon: BookOpen,
      type: 'info',
      content: {
        heading: 'Welcome to Problem Definition',
        description: 'We\'ll guide you through a structured approach to clearly define your problem using the 5W1H framework.',
        tips: [
          'Take your time with each step',
          'Be specific and objective',
          'Focus on facts, not assumptions',
          'Each field builds upon the previous'
        ]
      }
    },
    {
      id: 'process',
      title: 'The 5W1H Framework',
      icon: Target,
      type: 'info',
      content: {
        heading: 'Your Problem Definition Journey',
        description: 'We\'ll collect information using the proven 5W1H method:',
        framework: [
          { label: 'WHAT', description: 'The issue, equipment, or complaint', icon: AlertCircle },
          { label: 'WHERE', description: 'Location and context', icon: MapPin },
          { label: 'WHEN', description: 'Timing and sequence', icon: Clock },
          { label: 'WHO', description: 'People involved or affected', icon: Users },
          { label: 'HOW', description: 'Impact and effects', icon: HelpCircle },
          { label: 'WHY', description: 'Significance and need for solution', icon: Lightbulb }
        ]
      }
    },
    {
      id: 'what',
      title: 'WHAT - Define the Issue',
      icon: AlertCircle,
      type: 'input',
      field: 'what',
      content: {
        heading: 'What is the Problem?',
        description: 'Describe the specific issue, equipment, or complaint',
        prompts: [
          'What equipment, machine, or tool is involved?',
          'What is wrong? What is the complaint?',
          'What undesired behavior is occurring?'
        ],
        placeholder: 'Example: Aircraft cabin door seal failure causing pressurization issues...'
      }
    },
    {
      id: 'where',
      title: 'WHERE - Location & Context',
      icon: MapPin,
      type: 'input',
      field: 'where',
      content: {
        heading: 'Where is this Occurring?',
        description: 'Specify the location and operational context',
        prompts: [
          'Which unit, area, or department?',
          'What is the specific location of the defect?',
          'In what operational environment?'
        ],
        placeholder: 'Example: Forward cargo door, Aircraft 737-800, Maintenance Hangar B...'
      }
    },
    {
      id: 'when',
      title: 'WHEN - Timing & Sequence',
      icon: Clock,
      type: 'input',
      field: 'when',
      content: {
        heading: 'When Does This Occur?',
        description: 'Establish the timeline and operational phase',
        prompts: [
          'When does it occur? (Date and time)',
          'What shift or operation phase?',
          'At what stage of equipment lifecycle?'
        ],
        placeholder: 'Example: During pre-flight checks, Morning shift (0600-1400), Aircraft with 15,000+ flight hours...'
      }
    },
    {
      id: 'who',
      title: 'WHO - People Involved',
      icon: Users,
      type: 'input',
      field: 'who',
      content: {
        heading: 'Who is Involved or Affected?',
        description: 'Identify all relevant personnel and stakeholders',
        prompts: [
          'Who is involved? (Information, help, actions)',
          'Who is affected by this problem?',
          'Who needs to be informed?'
        ],
        placeholder: 'Example: Maintenance technicians, Flight crew, Ground operations, Passengers...'
      }
    },
    {
      id: 'how',
      title: 'HOW - Impact & Effects',
      icon: HelpCircle,
      type: 'input',
      field: 'how',
      content: {
        heading: 'How Are People/Systems Affected?',
        description: 'Describe the impact and consequences',
        prompts: [
          'How is the "What" or "Who" affected?',
          'What type of impact? (Safety, operational, financial)',
          'What classification or severity level?'
        ],
        placeholder: 'Example: Flight delays averaging 45 minutes, Safety risk during climb phase, Increased maintenance costs...'
      }
    },
    {
      id: 'why',
      title: 'WHY - Significance',
      icon: Lightbulb,
      type: 'input',
      field: 'why',
      content: {
        heading: 'Why is This Problem Significant?',
        description: 'Explain why this needs to be solved',
        prompts: [
          'Why has this problem arisen?',
          'Why is a solution needed?',
          'What are the consequences of inaction?'
        ],
        placeholder: 'Example: Regulatory compliance requirement, Passenger safety concern, Increasing operational costs...'
      }
    },
    {
      id: 'synthesis',
      title: 'Problem Statement Synthesis',
      icon: FileText,
      type: 'synthesis',
      content: {
        heading: 'Let\'s Build Your Problem Statement',
        description: 'We\'ll combine your inputs into a comprehensive problem statement'
      }
    },
    {
      id: 'review',
      title: 'Final Review',
      icon: CheckCircle,
      type: 'review',
      content: {
        heading: 'Review Your Problem Definition',
        description: 'Ensure your problem statement is complete and accurate'
      }
    }
  ];

  const currentCardData = cards[currentCard];
  const progress = ((currentCard + 1) / cards.length) * 100;

  // Initialize with existing data
  useEffect(() => {
    if (initialStatement) {
      setProblemData(prevState => ({
        ...prevState,
        ...initialStatement
      }));
    }
  }, [initialStatement]);

  // Generate rule-based problem statement (your existing logic)
  const generateFullStatement = () => {
    const { what, where, when, who, how, why } = problemData;
    return `The problem is ${what} occurring at ${where}. It was first noticed on ${when} and affects ${who}. The issue manifests by ${how}. This is significant because ${why}.`;
  };

  // AI Enhancement (similar to your EnhancedTextArea.js)
  const handleAIEnhancement = async () => {
    if (!problemData.full_statement.trim()) {
      setError('Please enter a problem statement before enhancing');
      return;
    }

    try {
      setIsEnhancing(true);
      setError('');

      const thread = await openai.beta.threads.create();
      
      await openai.beta.threads.messages.create(thread.id, {
        role: 'user',
        content: `Please improve the structure and clarity of this problem statement. 
          IMPORTANT RULES:
          - Only use information explicitly provided in the original text
          - Do not add any new details or assumptions
          - Focus on improving clarity, grammar, and organization
          - Maintain all original facts and specifics exactly as provided
          
          Provide your response in three parts:
          1. ENHANCED_TEXT: The restructured problem statement
          2. SHORT_DESCRIPTION: A brief title of the core problem, issue or event
          3. COMMENTS: Brief explanation of the structural improvements made
          
          Original text: ${problemData.full_statement}`
      });

      const run = await openai.beta.threads.runs.create(thread.id, {
        assistant_id: process.env.REACT_APP_OPENAI_ASSISTANT_ID,
        instructions: "You are helping to structure problem statements. Only use information explicitly provided by the user. Never add new details or make assumptions. Focus solely on improving clarity and organization of the existing content."
      });

      let runStatus = await openai.beta.threads.runs.retrieve(thread.id, run.id);
      while (runStatus.status !== 'completed') {
        await new Promise(resolve => setTimeout(resolve, 1000));
        runStatus = await openai.beta.threads.runs.retrieve(thread.id, run.id);
        
        if (runStatus.status === 'failed') {
          throw new Error('Enhancement failed');
        }
      }

      const messages = await openai.beta.threads.messages.list(thread.id);
      const response = messages.data[0].content[0].text.value;
      
      // Extract enhanced text, short description, and comments
      const enhancedMatch = response.match(/ENHANCED_TEXT:(.*?)(?=SHORT_DESCRIPTION:|$)/s);
      const shortDescMatch = response.match(/SHORT_DESCRIPTION:(.*?)(?=COMMENTS:|$)/s);
      const commentsMatch = response.match(/COMMENTS:(.*?)$/s);
      
      const enhancedText = enhancedMatch ? enhancedMatch[1].trim() : '';
      const shortDescription = shortDescMatch ? shortDescMatch[1].trim() : '';
      const comments = commentsMatch ? commentsMatch[1].trim() : '';

      if (enhancedText) {
        setProblemData(prev => ({
          ...prev,
          full_statement: enhancedText,
          short_description: shortDescription || prev.short_description
        }));

        // Send comments to AI Assistant chat
        if (comments) {
          addAssistantMessage(
            "Here are the structural improvements made to your problem statement:\n\n" + 
            comments
          );
        }
      }

    } catch (err) {
      console.error('Error enhancing text:', err);
      setError('Failed to enhance text. Please try again.');
    } finally {
      setIsEnhancing(false);
    }
  };

  // AI Validation (NEW - direct OpenAI call + assistant context)
  const handleAIValidation = async () => {
    if (!problemData.full_statement.trim()) {
      setError('Please enter a problem statement before validating');
      return;
    }

    setIsValidating(true);
    try {
      setError('');

      // Direct OpenAI call for validation
      const thread = await openai.beta.threads.create();
      
      await openai.beta.threads.messages.create(thread.id, {
        role: 'user',
        content: `Evaluate this problem statement against the 4 key criteria for well-written problem statements:

        1. FOCUSED ON GAP: Does it identify deviation from a requirement, norm, standard, or expectation?
        2. INDICATES EFFECT: Does it state what is wrong (not why it's wrong)?
        3. MEASURABLE: Does it include how often, how much, when, or other measurable aspects?
        4. POSITIVE MANNER: Is it stated positively, describing the issue rather than using "lack of" or negative phrasing?

        Problem Statement: "${problemData.full_statement}"

        Please provide:
        - A score from 0-100 for each criterion
        - Specific feedback explaining the score
        - Concrete suggestions for improvement (if needed)
        - An overall assessment

        Format your response as JSON with this structure:
        {
          "focusedOnGap": {"score": number, "feedback": "string", "suggestion": "string or null"},
          "indicatesEffect": {"score": number, "feedback": "string", "suggestion": "string or null"},
          "measurable": {"score": number, "feedback": "string", "suggestion": "string or null"},
          "positiveManner": {"score": number, "feedback": "string", "suggestion": "string or null"},
          "overallScore": number,
          "overallFeedback": "string"
        }`
      });

      const run = await openai.beta.threads.runs.create(thread.id, {
        assistant_id: process.env.REACT_APP_OPENAI_ASSISTANT_ID,
        instructions: "You are an expert in problem statement analysis. Evaluate based on the 4 criteria and provide actionable feedback. Be specific and constructive in your suggestions."
      });

      let runStatus = await openai.beta.threads.runs.retrieve(thread.id, run.id);
      while (runStatus.status !== 'completed') {
        await new Promise(resolve => setTimeout(resolve, 1000));
        runStatus = await openai.beta.threads.runs.retrieve(thread.id, run.id);
        
        if (runStatus.status === 'failed') {
          throw new Error('Validation failed');
        }
      }

      const messages = await openai.beta.threads.messages.list(thread.id);
      const response = messages.data[0].content[0].text.value;
      
      // Parse JSON response
      const jsonMatch = response.match(/\{.*\}/s);
      if (jsonMatch) {
        const validationResults = JSON.parse(jsonMatch[0]);
        setAiValidation(validationResults);

        // Feed context to assistant for follow-up questions
        updateContext({
          validationResults,
          problemStatement: problemData.full_statement,
          validationStep: true,
          currentCard: 'synthesis'
        });

        // Notify user that assistant has context
        addAssistantMessage(
          `I've evaluated your problem statement against the 4 key criteria. Overall score: ${validationResults.overallScore}/100. ` +
          "Ask me for specific guidance on improving any areas that need work!"
        );
      }

    } catch (error) {
      console.error('AI validation failed:', error);
      setError('AI validation failed. Please try again.');
    } finally {
      setIsValidating(false);
    }
  };

  const handleInputChange = (value) => {
    setProblemData(prev => ({
      ...prev,
      [currentCardData.field]: value
    }));
  };

  const handleNext = () => {
    if (currentCard < cards.length - 1) {
      // Trigger exit animation
      setCardTransition('exit');
      
      setTimeout(() => {
        // Auto-generate problem statement when reaching synthesis card
        if (currentCard === 7) { // Moving from 'why' to 'synthesis'
          const generatedStatement = generateFullStatement();
          setProblemData(prev => ({
            ...prev,
            full_statement: generatedStatement
          }));
        }
        
        // Mark current card as completed if it has content
        if (currentCardData.type === 'input' && problemData[currentCardData.field]?.trim()) {
          setCardCompletionStatus(prev => ({
            ...prev,
            [currentCard]: true
          }));
        }
        
        setCurrentCard(prev => prev + 1);
        setCardTransition('enter');
        
        // Return to center after brief delay
        setTimeout(() => setCardTransition('center'), 50);
      }, 200);
    }
  };

  const handlePrevious = () => {
    if (currentCard > 0) {
      // Trigger exit animation
      setCardTransition('exit-reverse');
      
      setTimeout(() => {
        setCurrentCard(prev => prev - 1);
        setCardTransition('enter-reverse');
        
        // Return to center after brief delay
        setTimeout(() => setCardTransition('center'), 50);
      }, 200);
    }
  };

  const handleSave = async () => {
    if (!projectId) return;

    setSaveStatus('Saving...');
    try {
      await supabase
        .from('problem_statements')
        .delete()
        .eq('project_id', projectId);

      const { error: insertError } = await supabase
        .from('problem_statements')
        .insert({
          ...problemData,
          project_id: projectId
        });

      if (insertError) throw insertError;

      setSaveStatus('Saved successfully');
      setTimeout(() => setSaveStatus(''), 3000);

      // Call completion callback if provided
      if (onComplete) {
        onComplete(problemData);
      }
    } catch (error) {
      console.error('Error saving problem statement:', error);
      setSaveStatus('Error saving');
      setTimeout(() => setSaveStatus(''), 3000);
    }
  };

  const isCardComplete = (cardIndex) => {
    const card = cards[cardIndex];
    if (card.type === 'input') {
      return problemData[card.field]?.trim().length > 0;
    }
    return cardCompletionStatus[cardIndex] || cardIndex < currentCard;
  };

  const renderProgressBar = () => (
    <div className="mb-6">
      <div className="flex justify-between items-center mb-2">
        <span className={`text-sm font-medium text-[${theme.colors.text.secondary}]`}>
          Step {currentCard + 1} of {cards.length}
        </span>
        <span className={`text-sm font-medium text-[${theme.colors.primary.main}]`}>
          {Math.round(progress)}% Complete
        </span>
      </div>
      <div className={`w-full bg-[${theme.colors.neutral.light}] rounded-full h-2`}>
        <div 
          className={`bg-gradient-to-r from-[${theme.colors.primary.main}] to-[${theme.colors.primary.hover}] h-2 rounded-full transition-all duration-300`}
          style={{ width: `${progress}%` }}
        />
      </div>
      
      {/* Step indicators */}
      <div className="flex justify-between mt-4">
        {cards.map((card, index) => {
          const IconComponent = card.icon;
          const isCompleted = isCardComplete(index);
          const isCurrent = index === currentCard;
          
          return (
            <div key={index} className="flex flex-col items-center">
              <div 
                className={`w-8 h-8 rounded-full flex items-center justify-center transition-all duration-200 ${
                  isCurrent 
                    ? `bg-[${theme.colors.primary.main}] text-white scale-110` 
                    : isCompleted
                    ? `bg-[${theme.colors.secondary.main}] text-white`
                    : `bg-[${theme.colors.neutral.main}] text-[${theme.colors.text.light}]`
                }`}
              >
                <IconComponent size={16} />
              </div>
              <span className={`text-xs mt-1 text-center text-[${theme.colors.text.secondary}] max-w-16`}>
                {card.title.split(' ')[0]}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );

  const renderInfoCard = () => (
    <div className="text-center">
      <div className="mb-6">
        <div className={`mx-auto w-16 h-16 bg-[${theme.colors.primary.light}] rounded-full flex items-center justify-center mb-4`}>
          <currentCardData.icon size={32} className={`text-[${theme.colors.primary.main}]`} />
        </div>
        <h2 className={`text-2xl font-bold text-[${theme.colors.text.primary}] mb-2`}>
          {currentCardData.content.heading}
        </h2>
        <p className={`text-[${theme.colors.text.secondary}] text-lg`}>
          {currentCardData.content.description}
        </p>
      </div>

      {currentCardData.content.tips && (
        <div className={`bg-[${theme.colors.primary.light}] rounded-lg p-4 mb-6`}>
          <h3 className={`font-semibold text-[${theme.colors.primary.main}] mb-3`}>💡 Quick Tips</h3>
          <ul className="text-left space-y-2">
            {currentCardData.content.tips.map((tip, index) => (
              <li key={index} className="flex items-start">
                <CheckCircle size={16} className={`text-[${theme.colors.primary.main}] mt-0.5 mr-2 flex-shrink-0`} />
                <span className={`text-[${theme.colors.primary.main}]`}>{tip}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {currentCardData.content.framework && (
        <div className="grid grid-cols-2 gap-4 mb-6">
          {currentCardData.content.framework.map((item, index) => (
            <Card key={index} className="p-4">
              <div className="flex items-center mb-2">
                <item.icon size={20} className={`text-[${theme.colors.primary.main}] mr-2`} />
                <span className={`font-semibold text-[${theme.colors.text.primary}]`}>{item.label}</span>
              </div>
              <p className={`text-sm text-[${theme.colors.text.secondary}]`}>{item.description}</p>
            </Card>
          ))}
        </div>
      )}
    </div>
  );

  const renderInputCard = () => {
    const currentValue = problemData[currentCardData.field] || '';
    const wordCount = currentValue.trim().split(/\s+/).filter(word => word.length > 0).length;
    
    // Light quality hints based on content
    const getQualityHint = () => {
      if (!currentValue.trim()) return null;
      
      const hints = {
        what: {
          good: currentValue.includes('equipment') || currentValue.includes('system') || currentValue.includes('failure'),
          suggestion: 'Consider mentioning specific equipment, systems, or failure modes'
        },
        where: {
          good: currentValue.includes('area') || currentValue.includes('location') || currentValue.includes('department'),
          suggestion: 'Try to be specific about the physical or operational location'
        },
        when: {
          good: currentValue.includes('during') || /\d/.test(currentValue) || currentValue.includes('shift'),
          suggestion: 'Include specific times, dates, or operational phases'
        },
        who: {
          good: currentValue.includes('crew') || currentValue.includes('technician') || currentValue.includes('staff'),
          suggestion: 'Specify roles, departments, or specific personnel involved'
        },
        how: {
          good: currentValue.includes('delay') || currentValue.includes('damage') || currentValue.includes('impact'),
          suggestion: 'Describe the specific effects or consequences observed'
        },
        why: {
          good: currentValue.includes('safety') || currentValue.includes('compliance') || currentValue.includes('cost'),
          suggestion: 'Explain the business or safety significance'
        }
      };
      
      return hints[currentCardData.field];
    };

    const qualityHint = getQualityHint();

    return (
      <div>
        <div className="text-center mb-6">
          <div className={`mx-auto w-16 h-16 bg-[${theme.colors.primary.light}] rounded-full flex items-center justify-center mb-4`}>
            <currentCardData.icon size={32} className={`text-[${theme.colors.primary.main}]`} />
          </div>
          <h2 className={`text-2xl font-bold text-[${theme.colors.text.primary}] mb-2`}>
            {currentCardData.content.heading}
          </h2>
          <p className={`text-[${theme.colors.text.secondary}]`}>
            {currentCardData.content.description}
          </p>
        </div>

        <div className={`bg-[${theme.colors.accent.light}] rounded-lg p-4 mb-6`}>
          <h3 className={`font-semibold text-[${theme.colors.accent.dark}] mb-3`}>🤔 Consider These Questions:</h3>
          <ul className="space-y-2">
            {currentCardData.content.prompts.map((prompt, index) => (
              <li key={index} className="flex items-start">
                <span className={`text-[${theme.colors.accent.main}] mr-2`}>•</span>
                <span className={`text-[${theme.colors.accent.dark}]`}>{prompt}</span>
              </li>
            ))}
          </ul>
        </div>

        <div className="mb-4">
          <div className="flex items-center justify-between mb-2">
            <label className={`block text-sm font-medium text-[${theme.colors.text.primary}]`}>
              Your Response:
            </label>
            <span className={`text-xs text-[${theme.colors.text.secondary}]`}>
              {wordCount} words
            </span>
          </div>
          <TextArea
            value={problemData[currentCardData.field] || ''}
            onChange={(e) => handleInputChange(e.target.value)}
            placeholder={currentCardData.content.placeholder}
            rows={4}
          />
        </div>

        {/* Light Quality Feedback */}
        {qualityHint && currentValue.trim() && (
          <div className={`p-3 rounded-lg border-l-4 ${
            qualityHint.good 
              ? `bg-[${theme.colors.secondary.light}] border-[${theme.colors.secondary.main}]` 
              : `bg-[${theme.colors.primary.light}] border-[${theme.colors.primary.main}]`
          }`}>
            <div className="flex items-center">
              {qualityHint.good ? (
                <CheckCircle size={16} className={`text-[${theme.colors.secondary.main}] mr-2`} />
              ) : (
                <Lightbulb size={16} className={`text-[${theme.colors.primary.main}] mr-2`} />
              )}
              <span className={`text-sm ${
                qualityHint.good 
                  ? `text-[${theme.colors.secondary.dark}]` 
                  : `text-[${theme.colors.primary.main}]`
              }`}>
                {qualityHint.good 
                  ? 'Good detail level!' 
                  : `Tip: ${qualityHint.suggestion}`
                }
              </span>
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderSynthesisCard = () => {
    return (
      <div>
        <div className="text-center mb-6">
          <div className={`mx-auto w-16 h-16 bg-[${theme.colors.secondary.light}] rounded-full flex items-center justify-center mb-4`}>
            <FileText size={32} className={`text-[${theme.colors.secondary.main}]`} />
          </div>
          <h2 className={`text-2xl font-bold text-[${theme.colors.text.primary}] mb-2`}>
            {currentCardData.content.heading}
          </h2>
          <p className={`text-[${theme.colors.text.secondary}]`}>
            {currentCardData.content.description}
          </p>
        </div>

        <div className="space-y-6">
          {/* Problem Title (Short Description) - First */}
          <div>
            <label className={`block text-sm font-medium text-[${theme.colors.text.primary}] mb-2`}>
              Problem Title (Short Description):
              <span className={`text-[${theme.colors.accent.main}] ml-1`}>*</span>
            </label>
            <Input
              value={problemData.short_description}
              onChange={(e) => setProblemData(prev => ({ ...prev, short_description: e.target.value }))}
              placeholder="Brief, clear title summarizing the problem..."
            />
            <p className={`text-xs text-[${theme.colors.text.secondary}] mt-1`}>
              Keep this concise - it will be used as the problem identifier
            </p>
          </div>
          
          {/* Full Problem Statement with AI Enhancement */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className={`block text-sm font-medium text-[${theme.colors.text.primary}]`}>
                Full Problem Statement:
                <span className={`text-[${theme.colors.accent.main}] ml-1`}>*</span>
              </label>
              <Button
                onClick={handleAIEnhancement}
                disabled={isEnhancing}
                variant="accent"
                className="flex items-center text-sm"
              >
                {isEnhancing ? (
                  <>
                    <div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full mr-2"></div>
                    Enhancing...
                  </>
                ) : (
                  <>
                    <Sparkles size={16} className="mr-1" />
                    Enhance
                  </>
                )}
              </Button>
            </div>
            <TextArea
              value={problemData.full_statement}
              onChange={(e) => setProblemData(prev => ({ ...prev, full_statement: e.target.value }))}
              placeholder="Detailed problem statement incorporating all the 5W1H elements..."
              rows={6}
            />
            <p className={`text-xs text-[${theme.colors.text.secondary}] mt-1`}>
              Click ✨ to improve structure & grammar, plus auto-generate the short description above
            </p>
          </div>
        </div>

        {/* AI Validation Section */}
        <div className={`mt-8 p-6 bg-gradient-to-br from-[${theme.colors.accent.light}] to-white rounded-lg border border-[${theme.colors.accent.main}]`}>
          <div className="flex items-center justify-between mb-4">
            <div>
              <h4 className={`font-semibold text-[${theme.colors.accent.dark}] flex items-center`}>
                <CheckCircle size={20} className="mr-2" />
                Problem Statement Quality Check
              </h4>
              <p className={`text-sm text-[${theme.colors.accent.main}] mt-1`}>
                Validate your problem statement against the 4 key criteria
              </p>
            </div>
            <Button
              onClick={handleAIValidation}
              disabled={!problemData.full_statement?.trim() || isValidating}
              variant="primary"
              className="flex items-center"
            >
              {isValidating ? (
                <>
                  <div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full mr-2"></div>
                  Validating...
                </>
              ) : (
                <>
                  <CheckCircle size={16} className="mr-2" />
                  Validate Statement
                </>
              )}
            </Button>
          </div>

          {aiValidation && (
            <div className="space-y-4">
              {/* Overall Score */}
              <Card className="p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className={`font-medium text-[${theme.colors.text.primary}]`}>Overall Quality Score</span>
                  <div className={`${
                    aiValidation.overallScore >= 90 ? `text-[${theme.colors.secondary.main}]` :
                    aiValidation.overallScore >= 75 ? `text-[${theme.colors.accent.main}]` : `text-[${theme.colors.accent.main}]`
                  }`}>
                    <AnimatedScore targetScore={aiValidation.overallScore} suffix="/100" />
                  </div>
                </div>
                <p className={`text-sm text-[${theme.colors.text.secondary}]`}>{aiValidation.overallFeedback}</p>
              </Card>

              {/* 4 Criteria Details */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {[
                  { key: 'focusedOnGap', title: '1. Focused on the Gap', desc: 'Identifies deviation from requirement/norm' },
                  { key: 'indicatesEffect', title: '2. Indicates the Effect', desc: 'States what is wrong, not why' },
                  { key: 'measurable', title: '3. Measurable', desc: 'Includes how often, how much, when' },
                  { key: 'positiveManner', title: '4. Positive Manner', desc: 'Describes issue without negative phrasing' }
                ].map(({ key, title, desc }, index) => {
                  const criterion = aiValidation[key];
                  const scoreColor = criterion.score >= 90 ? `text-[${theme.colors.secondary.main}]` :
                                   criterion.score >= 75 ? `text-[${theme.colors.accent.main}]` : `text-[${theme.colors.accent.main}]`;
                  
                  return (
                    <Card key={key} className={`p-4 transition-all duration-500 hover:shadow-lg transform hover:-translate-y-1`}
                          style={{ animationDelay: `${index * 150}ms` }}>
                      <div className="flex items-center justify-between mb-2">
                        <div>
                          <h5 className={`font-medium text-[${theme.colors.text.primary}] text-sm`}>{title}</h5>
                          <p className={`text-xs text-[${theme.colors.text.secondary}]`}>{desc}</p>
                        </div>
                        <div className={`${scoreColor} relative`}>
                          <AnimatedScore 
                            targetScore={criterion.score} 
                            duration={1500 + (index * 200)}
                          />
                          {/* Score ring animation */}
                          <div className={`absolute inset-0 rounded-full border-2 ${
                            criterion.score >= 90 ? `border-[${theme.colors.secondary.main}]` :
                            criterion.score >= 75 ? `border-[${theme.colors.accent.main}]` : `border-[${theme.colors.accent.main}]`
                          } animate-pulse`} 
                          style={{ 
                            animationDelay: `${1500 + (index * 200)}ms`,
                            animationDuration: '2s',
                            animationIterationCount: '1'
                          }}></div>
                        </div>
                      </div>
                      <p className={`text-sm text-[${theme.colors.text.secondary}] mb-2`}>{criterion.feedback}</p>
                      {criterion.suggestion && (
                        <div className={`bg-[${theme.colors.primary.light}] rounded p-2 mt-2 transform transition-all duration-300 opacity-0 animate-fadeIn`}
                             style={{ animationDelay: `${2000 + (index * 200)}ms`, animationFillMode: 'forwards' }}>
                          <p className={`text-xs text-[${theme.colors.primary.main}]`}>
                            <strong>Suggestion:</strong> {criterion.suggestion}
                          </p>
                        </div>
                      )}
                    </Card>
                  );
                })}
              </div>

              {/* Link to Chat Assistant */}
              <Card className={`p-4 border-[${theme.colors.primary.main}]`}>
                <div className={`flex items-center text-[${theme.colors.primary.main}]`}>
                  <Lightbulb size={16} className="mr-2" />
                  <span className="text-sm font-medium">Need more detailed guidance?</span>
                </div>
                <p className={`text-xs text-[${theme.colors.primary.main}] mt-1`}>
                  Your AI assistant now has context about these validation results. Ask for specific help improving any criteria.
                </p>
              </Card>
            </div>
          )}
        </div>

        {/* Completeness Summary */}
        <div className={`mt-6 p-4 bg-[${theme.colors.neutral.light}] rounded-lg`}>
          <h4 className={`font-medium text-[${theme.colors.text.primary}] mb-2`}>Input Completeness:</h4>
          <div className="grid grid-cols-2 gap-2">
            {[
              { label: 'Problem Title', complete: !!problemData.short_description?.trim() },
              { label: 'Full Statement', complete: !!problemData.full_statement?.trim() },
              { label: 'What Defined', complete: !!problemData.what?.trim() },
              { label: 'Where Defined', complete: !!problemData.where?.trim() },
              { label: 'When Defined', complete: !!problemData.when?.trim() },
              { label: 'Who Defined', complete: !!problemData.who?.trim() }
            ].map(({ label, complete }) => (
              <div key={label} className="flex items-center text-sm">
                <CheckCircle 
                  size={16} 
                  className={`mr-2 ${complete ? `text-[${theme.colors.secondary.main}]` : `text-[${theme.colors.neutral.main}]`}`} 
                />
                <span className={complete ? `text-[${theme.colors.secondary.main}]` : `text-[${theme.colors.text.secondary}]`}>
                  {label}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  const renderReviewCard = () => (
    <div>
      <div className="text-center mb-6">
        <div className={`mx-auto w-16 h-16 bg-[${theme.colors.secondary.light}] rounded-full flex items-center justify-center mb-4`}>
          <CheckCircle size={32} className={`text-[${theme.colors.secondary.main}]`} />
        </div>
        <h2 className={`text-2xl font-bold text-[${theme.colors.text.primary}] mb-2`}>
          Problem Definition Complete!
        </h2>
        <p className={`text-[${theme.colors.text.secondary}]`}>
          Review your problem definition before proceeding
        </p>
      </div>

      <div className={`bg-[${theme.colors.neutral.light}] rounded-lg p-6`}>
        <h3 className={`font-semibold text-[${theme.colors.text.primary}] mb-4 text-center`}>Your Problem Definition Summary</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          {[
            { label: 'WHAT', value: problemData.what, icon: AlertCircle },
            { label: 'WHERE', value: problemData.where, icon: MapPin },
            { label: 'WHEN', value: problemData.when, icon: Clock },
            { label: 'WHO', value: problemData.who, icon: Users },
            { label: 'HOW', value: problemData.how, icon: HelpCircle },
            { label: 'WHY', value: problemData.why, icon: Lightbulb }
          ].map(({ label, value, icon: Icon }) => (
            <Card key={label} className="p-3">
              <div className="flex items-center mb-2">
                <Icon size={16} className={`text-[${theme.colors.primary.main}] mr-2`} />
                <span className={`font-medium text-[${theme.colors.text.primary}]`}>{label}:</span>
              </div>
              <p className={`text-sm text-[${theme.colors.text.secondary}]`}>{value || 'Not provided'}</p>
            </Card>
          ))}
        </div>

        <Card className="p-4">
          <h4 className={`font-medium text-[${theme.colors.text.primary}] mb-2`}>Final Problem Statement:</h4>
          <p className={`text-[${theme.colors.text.secondary}]`}>{problemData.full_statement}</p>
        </Card>

        <div className="mt-6 flex gap-4">
          <Button
            onClick={handleSave}
            variant="primary"
            className="flex-1"
          >
            Save & Complete
          </Button>
        </div>
      </div>
    </div>
  );

  const renderCurrentCard = () => {
    switch (currentCardData.type) {
      case 'info':
        return renderInfoCard();
      case 'input':
        return renderInputCard();
      case 'synthesis':
        return renderSynthesisCard();
      case 'review':
        return renderReviewCard();
      default:
        return null;
    }
  };

  const canProceed = () => {
    if (currentCardData.type === 'input') {
      return problemData[currentCardData.field]?.trim().length > 0;
    }
    if (currentCardData.type === 'synthesis') {
      // Both title and full statement required for synthesis step
      return problemData.short_description?.trim().length > 0 && 
             problemData.full_statement?.trim().length > 0;
    }
    return true;
  };

  return (
    <div className="max-w-4xl mx-auto">
      {renderProgressBar()}
      
      <Card className="p-8 min-h-[500px] relative overflow-hidden">
        {/* Card Content with Smooth Transitions */}
        <div className={`transition-all duration-300 ease-in-out transform ${
          cardTransition === 'center' ? 'translate-x-0 opacity-100' :
          cardTransition === 'exit' ? '-translate-x-full opacity-0' :
          cardTransition === 'exit-reverse' ? 'translate-x-full opacity-0' :
          cardTransition === 'enter' ? 'translate-x-full opacity-0' :
          cardTransition === 'enter-reverse' ? '-translate-x-full opacity-0' :
          'translate-x-0 opacity-100'
        }`}>
          {renderCurrentCard()}
        </div>
        
        <div className={`flex justify-between items-center mt-8 pt-6 border-t border-[${theme.colors.border.light}]`}>
          <Button
            onClick={handlePrevious}
            disabled={currentCard === 0}
            variant="secondary"
            className="flex items-center"
          >
            <ChevronLeft size={20} className="mr-2" />
            Previous
          </Button>
          
          <span className={`text-sm text-[${theme.colors.text.secondary}]`}>
            {currentCard + 1} / {cards.length}
          </span>
          
          <Button
            onClick={handleNext}
            disabled={currentCard === cards.length - 1 || !canProceed()}
            variant="primary"
            className="flex items-center"
          >
            Next
            <ChevronRight size={20} className="ml-2" />
          </Button>
        </div>
      </Card>

      {/* Error and Save Status */}
      {error && (
        <div className={`mt-4 px-4 py-3 rounded-lg bg-[${theme.colors.accent.light}] border border-[${theme.colors.accent.main}] text-[${theme.colors.accent.dark}]`}>
          {error}
        </div>
      )}

      {saveStatus && (
        <div className={`mt-4 text-center px-4 py-2 rounded-lg border ${
          saveStatus.includes('Error')
            ? `bg-[${theme.colors.accent.light}] border-[${theme.colors.accent.main}] text-[${theme.colors.accent.dark}]`
            : `bg-[${theme.colors.secondary.light}] border-[${theme.colors.secondary.main}] text-[${theme.colors.secondary.dark}]`
        }`}>
          {saveStatus}
        </div>
      )}
    </div>
  );
};

export default FlashcardProblemDefinition;